import type React from "react";
import { useEffect, useRef, useState } from "react";

import type { DateSelectArg, DatesSetArg, EventClickArg, EventDropArg } from "@fullcalendar/core";
import svLocale from "@fullcalendar/core/locales/sv";
import dayGridPlugin from "@fullcalendar/daygrid";
import type { EventResizeDoneArg } from "@fullcalendar/interaction";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { Box, Collapse, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useGetAppointmentsV2 } from "api/services/appointment/hooks";
import { useCalendarContext } from "contexts/CalendarContext";
import { useProfileContext } from "contexts/ProfileContext";
import { CALENDAR_TIMES } from "routes/calendar/helpers/misc";
import { BaseButton } from "shared/atoms/BaseButton";
import { getActionButtonColorStyles } from "shared/atoms/BaseButton/BaseButton";
import { View } from "shared/containers/View";
import { Notifications } from "shared/molecules/MUIDataGrid/MUIDataGrid";
import { useLocalizedDate } from "utils/date";
import { useWindowSize } from "utils/hooks";
import { userIsAdmin } from "utils/profile/profileHelper";

import { AppointmentDetails, BookingIntervalDetails } from "../EventDetails";
import { FormsContainer } from "../Forms";
import { PersonnelAutocomplete } from "../PersonnelAutocomplete";

import { AddButton } from "./components/AddButton";
import { Filters } from "./components/Filters";
import { renderDayHeaderContent, transformAppointments } from "./helpers";
import { EventContent } from "./helpers/EventContent";
import { transformBookingIntervals } from "./helpers/transformBookingIntervals";
import { useHours } from "./hooks/useHours";

type CalendarView = "timeGridDay" | "timeGridWeek";

export interface SetSelectArgs {
  start: Date;
  end: Date;
}

interface Props {
  className?: string;
  customHeader?: React.ReactNode;
  customOnSelect?: (serviceID: number | undefined) => void;
  fullScreen?: boolean;
  initialDate?: SetSelectArgs;
  interactive?: boolean;
  lockedView?: CalendarView;
  navigateToCalendar?: boolean;
  showHeader?: boolean;
  showWidgetHeader?: boolean;
}

export const Calendar: React.FC<Props> = ({
  className,
  customHeader,
  customOnSelect,
  fullScreen = true,
  initialDate,
  interactive = true,
  lockedView,
  navigateToCalendar = false,
  showHeader = false,
  showWidgetHeader = false,
}) => {
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const navigate = useNavigate();
  const { isLargeScreen } = useWindowSize();
  const { format } = useLocalizedDate();
  const {
    selectedAppointment,
    setSelectedAppointment,
    selectedBookingInterval,
    setSelectedBookingInterval,
    selectedPersonnel,
    setSelectedPatient,
    activeForm,
    setActiveForm,
    selectedDate,
    setSelectedDate,
    showMyAvailability,
    showTherapistAvailability,
    showCanceledAppointments,
    showRescheduledAppointments,
    showWeekends,
  } = useCalendarContext();

  const [showEventDetails, setShowEventDetails] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [view, setView] = useState<"day" | "week">(interactive ? "week" : "day");
  const [calendarView] = useState<CalendarView>(isLargeScreen ? "timeGridWeek" : "timeGridDay");
  const [defaultServiceID, setDefaultServiceID] = useState<number | undefined>();
  const calendarRef = useRef<InstanceType<typeof FullCalendar>>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  invariant(profile);

  const isAdmin = userIsAdmin(profile);

  const { openingHoursString, closingHoursString } = useHours();

  const { data, refetch: refetchAppointments } = useGetAppointmentsV2(
    {
      userId: profile.id,
      startDate: encodeURIComponent(startDate),
      view,
      isAdmin,
      onlyTherapistAppointments: !interactive,
    },
    { enabled: !!startDate }
  );

  const appointments = data?.appointments ?? [];
  const bookingIntervals = data?.booking_intervals ?? [];

  const transformedAppointments = transformAppointments({
    appointments,
    isAdmin,
    selectedPersonnel,
    selectedAppointment,
    showCanceledAppointments,
    showRescheduledAppointments,
    ianaTimeZone: profile.iana_timezone,
  });
  const transformedBookingIntervals = transformBookingIntervals({
    bookingIntervals,
    isAdmin,
    selectedPersonnel,
    showMyAvailability,
    showTherapistAvailability,
    unavailableTitle: t("booking.calendar.event_title.blocked_time"),
    userID: profile.id,
    ianaTimeZone: profile.iana_timezone,
  });
  const events = [...transformedAppointments, ...transformedBookingIntervals];

  const onSelect = (args: DateSelectArg) => {
    if (args.jsEvent) {
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        const calendarEvents = calendarApi.getEvents();
        const bookingInterval = calendarEvents
          .filter(event => event.extendedProps.type === "booking_interval")
          .find(event => {
            if (event.start && event.end) {
              return event.start <= args.start && event.end >= args.end;
            }
            return false;
          });

        setDefaultServiceID(bookingInterval?.extendedProps.serviceID);
        setSelectedDate({ start: args.start, startStr: args.startStr, end: args.end, endStr: args.endStr });
        if (customOnSelect) {
          customOnSelect(bookingInterval?.extendedProps.serviceID);
        } else if (!activeForm && !selectedBookingInterval) {
          setActiveForm("book_appointment");
        }
      }
    }
  };

  const setSelect = (args: SetSelectArgs | null) => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      if (args) {
        setTimeout(() => {
          calendarApi.gotoDate(args.start);
          calendarApi.select(args);
        }, 0);
      } else {
        calendarApi.unselect();
      }
    }
  };

  useEffect(() => {
    if (initialDate) {
      setSelect(initialDate);
    }
  }, [initialDate]);

  const getHeaderToolbar = () => {
    if (lockedView) {
      return {
        left: "",
        center: "prev next",
        right: "",
      };
    }

    if (interactive) {
      return {
        left: "prev title next",
        center: "",
        right: isLargeScreen ? "timeGridDay,timeGridWeek" : "",
      };
    }

    return false;
  };

  const onDatesSet = (args: DatesSetArg) => {
    const inputDate = args.start;
    const utcEquivalent = new Date(inputDate.getTime() - inputDate.getTimezoneOffset() * 60000);
    const utcDatetimeString = utcEquivalent.toISOString();
    setStartDate(utcDatetimeString);

    if (args.view.type === "timeGridDay") {
      setView("day");
    } else if (args.view.type === "timeGridWeek") {
      setView("week");
    }
  };

  const onEventClick = (args: EventClickArg) => {
    args.jsEvent.preventDefault();

    if (args.event.extendedProps.type === "booking_interval") {
      const clickedbookingInterval = bookingIntervals.find(a => a.id === args.event.extendedProps.bookingIntervalID);
      if (clickedbookingInterval) {
        setSelectedBookingInterval(clickedbookingInterval);
        setSelectedAppointment(null);
        setShowEventDetails(true);
      }
    } else {
      const clickedAppointment = appointments.find(a => a.id === args.event.extendedProps.eventId);
      if (clickedAppointment) {
        setSelectedAppointment(clickedAppointment);
        setSelectedBookingInterval(null);
        setShowEventDetails(true);
      }
    }
  };

  const onEventDrop = (args: EventDropArg | EventResizeDoneArg) => {
    const {
      event: { start, startStr, end, endStr, extendedProps },
    } = args;

    if (extendedProps.type === "appointment") {
      const droppedAppointment = appointments.find(a => a.id === extendedProps.eventId);
      if (droppedAppointment && start && startStr && end && endStr) {
        setSelectedAppointment(droppedAppointment);
        setSelectedDate({
          start,
          startStr,
          end,
          endStr,
        });
        setActiveForm("book_appointment");
      }
    } else if (extendedProps.type === "booking_interval") {
      const droppedBookingInterval = bookingIntervals.find(a => a.id === extendedProps.bookingIntervalID);
      if (droppedBookingInterval && start && startStr && end && endStr) {
        setSelectedBookingInterval(droppedBookingInterval);
        setSelectedDate({
          start,
          startStr,
          end,
          endStr,
        });
        setActiveForm("availability");
      }
    }
  };

  const onWindowResize = () => {
    if (!lockedView && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.changeView(isLargeScreen ? "timeGridWeek" : "timeGridDay");
    }
  };

  let actionButtons = showHeader ? [<AddButton key="add-button" />] : [];
  if (isAdmin) {
    actionButtons = [
      <BaseButton
        variant="action"
        dataTestID="booking-services"
        text={t("booking.services.title")}
        icon="cog"
        onClick={() => {
          navigate("services");
        }}
      />,
      ...actionButtons,
    ];
  }

  return (
    <>
      {showWidgetHeader && (
        <WidgetHeader $isCollapsed={isCollapsed}>
          <Stack direction="row" alignItems="center">
            <Typography variant="h3">{t("booking.calendar.header")}</Typography>
            <Notifications>{format(new Date(), "eee d")}</Notifications>
          </Stack>
          <Stack direction="row" spacing={1}>
            <BaseButton
              variant="action"
              icon="calendar"
              onClick={() => navigate("/calendar")}
              text={t("booking.calendar.see_full_schedule")}
            />
            <ChevronButton $isCollapsed={isCollapsed}>
              <BaseButton variant="action" icon="chevron-down" onClick={() => setIsCollapsed(prev => !prev)} />
            </ChevronButton>
          </Stack>
        </WidgetHeader>
      )}
      <Collapse in={!isCollapsed}>
        <Box display="flex">
          <View
            title={t("views.calendar")}
            actionButtons={actionButtons}
            hideHeader={!showHeader}
            removePadding={!showHeader}
            fullScreen={fullScreen}
          >
            <CalendarContainer className={className} $interactive={interactive}>
              {customHeader}

              {showHeader && isAdmin && <PersonnelAutocomplete />}

              <CalendarWrapper>
                {showHeader && <Filters />}

                <FullCalendar
                  ref={calendarRef}
                  allDaySlot={false}
                  buttonText={{
                    day: t("common.day"),
                    week: t("common.week_num", { number: undefined }),
                  }}
                  contentHeight={1000}
                  dayHeaderFormat={{ weekday: "short" }}
                  dayHeaderContent={renderDayHeaderContent}
                  dayHeaders={!showWidgetHeader}
                  displayEventTime={false}
                  editable={interactive}
                  events={events}
                  eventContent={eventContentArgs => (
                    <EventContent eventContentArgs={eventContentArgs} isAdmin={isAdmin} isDayView={view === "day"} />
                  )}
                  expandRows
                  firstDay={1}
                  headerToolbar={getHeaderToolbar()}
                  initialView={lockedView || (interactive ? calendarView : "timeGridDay")}
                  locale={profile.language_code}
                  locales={[svLocale]}
                  longPressDelay={isLargeScreen ? 1 : 250}
                  nowIndicator
                  plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin]}
                  selectable={interactive}
                  select={interactive ? onSelect : undefined}
                  selectMirror
                  selectConstraint={{
                    startTime: openingHoursString || CALENDAR_TIMES.start,
                    endTime: closingHoursString || CALENDAR_TIMES.end,
                  }}
                  slotLabelFormat={{ hour: "numeric", minute: "numeric", omitZeroMinute: false }}
                  slotLabelInterval={{ hours: 1 }}
                  slotMinTime={openingHoursString || CALENDAR_TIMES.start}
                  slotMaxTime={closingHoursString || CALENDAR_TIMES.end}
                  titleFormat={{ year: "numeric", month: "short" }}
                  unselectAuto={Boolean(activeForm)}
                  datesSet={onDatesSet}
                  eventClick={onEventClick}
                  eventDrop={onEventDrop}
                  eventResize={onEventDrop}
                  eventOrder={["-type", "availability"]}
                  eventOrderStrict
                  eventMinHeight={20}
                  weekends={showWeekends}
                  weekNumbers
                  windowResize={interactive ? onWindowResize : undefined}
                  eventDidMount={event => {
                    if (event.event.extendedProps.boxShadow) {
                      // eslint-disable-next-line no-param-reassign
                      event.el.style.boxShadow = event.event.extendedProps.boxShadow;
                    }
                  }}
                />
              </CalendarWrapper>
            </CalendarContainer>
          </View>

          {selectedAppointment && showEventDetails && (
            <AppointmentDetails
              appointment={selectedAppointment}
              navigateToCalendar={navigateToCalendar}
              onClose={() => {
                setSelectedAppointment(null);
                setShowEventDetails(false);
              }}
              editBooking={() => {
                setSelectedDate(null);
                setActiveForm("book_appointment");
                setShowEventDetails(false);
              }}
            />
          )}

          {selectedBookingInterval && showEventDetails && (
            <BookingIntervalDetails
              bookingInterval={selectedBookingInterval}
              navigateToCalendar={navigateToCalendar}
              onClose={() => {
                setSelectedBookingInterval(null);
                setShowEventDetails(false);
              }}
              onEditBookingInterval={() => {
                setSelectedDate(null);
                setActiveForm("availability");
                setShowEventDetails(false);
              }}
            />
          )}

          {!navigateToCalendar && (
            <FormsContainer
              defaultServiceID={defaultServiceID}
              onCloseBookingForm={() => {
                setSelectedDate(null);
                setSelectedAppointment(null);
                setSelectedBookingInterval(null);
                setSelectedPatient(null);
                setActiveForm(null);
                setSelect(null);
              }}
              selectedDate={selectedDate}
              setSelect={setSelect}
              refetchAppointments={refetchAppointments}
            >
              <Outlet />
            </FormsContainer>
          )}
        </Box>
      </Collapse>
    </>
  );
};

const WidgetHeader = styled.div<{ $isCollapsed?: boolean }>`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  border-radius: ${({ $isCollapsed }) => ($isCollapsed ? "12px" : "12px 12px 0 0")};
  padding: 24px;
  background-color: ${props => props.theme.colors.white};
`;

const ChevronButton = styled.div<{ $isCollapsed: boolean }>`
  display: flex;
  svg {
    transition: transform 0.2s ease;
    transform: ${({ $isCollapsed }) => ($isCollapsed ? "rotateX(0deg)" : "rotateX(-180deg)")};
  }
`;

const CalendarWrapper = styled.div`
  position: relative;
`;

const CalendarContainer = styled.div<{ $interactive?: boolean }>`
  flex: 1;

  --fc-border-color: ${({ theme }) => theme.colors.redesign.db10};
  --now-indicator-color: ${({ theme }) => theme.colors.redesign.r100};
  --now-dot-size: 7px;

  :is(.fc-now-indicator-arrow, .fc-timegrid-now-indicator-arrow) {
    display: none !important;
  }

  :is(.fc-now-indicator-line, .fc-timegrid-now-indicator-line) {
    position: relative !important;
    border-top: 1px solid var(--now-indicator-color) !important;
    z-index: 1000 !important;
  }

  :is(.fc-now-indicator-line, .fc-timegrid-now-indicator-line)::before,
  :is(.fc-now-indicator-line, .fc-timegrid-now-indicator-line)::after {
    content: "";
    position: absolute;
    top: 50%;
    width: var(--now-dot-size);
    height: var(--now-dot-size);
    border-radius: 999px;
    background: var(--now-indicator-color);
    transform: translateY(-50%);
  }

  :is(.fc-now-indicator-line, .fc-timegrid-now-indicator-line)::before {
    left: calc(-1 * var(--now-dot-size) / 2);
  }

  :is(.fc-now-indicator-line, .fc-timegrid-now-indicator-line)::after {
    right: calc(-1 * var(--now-dot-size) / 2);
  }

  .fc-scrollgrid-section-body > td {
    border: none;
  }

  .fc-theme-standard .fc-scrollgrid {
    border: none;

    table {
      border-top-style: initial;
    }
  }

  .fc-view {
    padding: 24px;
    background: ${({ theme }) => theme.components.calendar.timegrid_bg_today} !important;
    border-radius: ${({ $interactive }) => ($interactive ? "12px" : "0 0 12px 12px")};
    ${props => props.theme.belowBreakpoint} {
      padding: 16px;
    }
  }

  .fc-scrollgrid-section-body table {
    border-right: 1px solid ${({ theme }) => theme.colors.redesign.db20};
    border-bottom: 1px solid ${({ theme }) => theme.colors.redesign.db20};
  }

  .fc-timegrid-col.fc-day-today {
    background: ${({ theme }) => theme.components.calendar.timegrid_bg_today} !important;
  }

  .fc-daygrid-day.fc-day-today {
    background: ${({ theme }) => theme.components.calendar.daygrid_bg_today};
  }

  .fc-header-toolbar.fc-toolbar {
    width: 100%;
    justify-content: space-between;
    margin-bottom: 16px;
    padding-right: 56px;
    position: relative;
  }

  .fc-toolbar-title {
    font-size: 16px;
    font-weight: 400;
    line-height: 1;
    text-transform: capitalize;
    color: ${({ theme }) => theme.colors.redesign.db100};
  }

  .fc-timegrid-axis-frame {
    justify-content: center;
  }

  .fc-timegrid-axis {
    border: none;
  }

  colgroup col {
    border-bottom: 1px solid ${({ theme }) => theme.colors.white};
  }

  .fc-timegrid-slot-label,
  .fc-timegrid-slot-minor {
    border: none;
  }

  .fc-col-header-cell {
    padding: 16px 4px 8px;
    border: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.redesign.db10};
  }

  .fc-col-header-cell-cushion {
    pointer-events: none;
  }

  .fc-scrollgrid-section th {
    border: none;
  }

  .fc-timegrid-axis-cushion {
    color: ${({ theme }) => theme.colors.redesign.db100};
    font-size: 14px;
    font-weight: 600;
  }

  .fc-timegrid-slot-label-cushion {
    padding: 0 12px 0 16px;
    font-size: 14px;
  }

  .fc-button {
    transition: 0.2s;
  }

  .fc-button-primary {
    padding: 7px 10px;
    color: ${({ theme }) => theme.components.calendar.btn_color};
    background: ${({ theme }) => theme.components.calendar.btn_bg};
    border: 1px solid ${({ theme }) => theme.components.calendar.btn_bg} !important;
    border-radius: 8px;

    &:focus,
    &:hover,
    &:active {
      background: ${({ theme }) => theme.components.calendar.btn_bg_hover};
      box-shadow: none !important;
    }
  }

  .fc-timeGridDay-button,
  .fc-timeGridWeek-button {
    margin-left: 0 !important;
    padding: 12px 16px;
    box-sizing: border-box;
    font-size: 14px;
    line-height: 1;

    ${getActionButtonColorStyles(true, "fc-button-active")}
  }

  .fc-button-active {
    background: ${({ theme }) => theme.components.calendar.btn_bg} !important;
  }

  .fc-toolbar-chunk:nth-of-type(1) {
    display: flex;
    align-items: center;
  }

  .fc-next-button,
  .fc-prev-button {
    margin: 0 !important;
    border-radius: 8px 0 0 8px;
    padding: 0 16px;
    box-sizing: border-box;
    line-height: 1;
    height: 40px;

    ${getActionButtonColorStyles(true)}
  }

  .fc-next-button {
    border-radius: 0 8px 8px 0;
  }

  .fc-prev-button + .fc-toolbar-title {
    margin: 0 !important;
    background-color: ${({ theme }) => theme.colors.white};
    padding: 0 16px;
    border-top: 1px solid ${({ theme }) => theme.colors.redesign.db20} !important;
    border-bottom: 1px solid ${({ theme }) => theme.colors.redesign.db20} !important;
    height: 40px;
    display: flex;
    align-items: center;
  }

  .fc-event {
    width: 85%;
    border-width: 2px;
  }

  .fc-event-mirror {
    width: 100%;
    background-color: ${({ theme }) => theme.colors.redesign.b40};
    border-color: ${({ theme }) => theme.colors.redesign.b40};
    opacity: 0.75;
  }

  .fc-event-main > div {
    height: 100%;
  }

  .fc-timegrid-event-short * {
    font-size: 10px;
  }

  .fc-timegrid-event-short .calendar-styled-initials {
    font-size: 8px;
  }

  ${props => props.theme.belowBreakpoint} {
    margin: auto;

    .fc-header-toolbar.fc-toolbar {
      align-items: center;
      flex-direction: column;
      padding: 0;
      margin-bottom: 8px;

      div:first-child {
        width: 100%;
      }

      & > * > :not(:first-child) {
        margin-left: 0;
      }
    }
  }
`;
